import Agilite from 'agilite'
import { sendRecord } from '../../../lib/websocket-utils'
import store from '../../../../store'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

/**
 * Get invoice communications data
 * @param {string} invoiceRef - Invoice reference ID
 * @returns {Promise} Communications data
 */
export const getInvoiceComms = (invoiceRef) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const filter = {
          invoiceRef,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('invoice_communications', 'read', {
          filter: JSON.stringify(filter)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Create new invoice communications record
 * @param {Object} data - Communications data
 * @returns {Promise} Created communications record
 */
export const createInvoiceComms = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('invoice_communications', 'create', {
          data: JSON.stringify(data)
        })

        // Broadcast creation via WebSocket
        if (response.data) {
          sendRecord(response.data, 'invoice_communications', 'create')
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update invoice communications record
 * @param {string} recordId - Record ID
 * @param {Object} data - Updated data
 * @returns {Promise} Updated communications record
 */
export const updateInvoiceComms = (recordId, data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute('invoice_communications', 'update', {
          recordId,
          data: JSON.stringify(data)
        })

        // Broadcast update via WebSocket
        if (response.data) {
          sendRecord(response.data, 'invoice_communications', 'update')
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Add a chat message to communications record
 * @param {string} recordId - Record ID
 * @param {Object} messageData - Message data
 * @returns {Promise} Updated communications record
 */
export const addChatMessage = (recordId, messageData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // First get the current record
        const currentRecord = await agilite.Connectors.execute('invoice_communications', 'read_by_id', {
          recordId
        })

        if (!currentRecord.data) {
          reject(new Error('Communications record not found'))
          return
        }

        // Add the new message to the chatMessages array
        const updatedData = {
          ...currentRecord.data,
          chatMessages: [...(currentRecord.data.chatMessages || []), messageData],
          modifiedAt: new Date().toISOString(),
          modifiedBy: store.getState().auth.agiliteUser?._id
        }

        // Update the record
        const response = await agilite.Connectors.execute('invoice_communications', 'update', {
          recordId,
          data: JSON.stringify(updatedData)
        })

        // Broadcast update via WebSocket
        if (response.data) {
          sendRecord(response.data, 'invoice_communications', 'message_added')
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update communication status (email, SMS, WhatsApp)
 * @param {string} recordId - Record ID
 * @param {Object} statusData - Status update data
 * @returns {Promise} Updated communications record
 */
export const updateCommStatus = (recordId, statusData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // First get the current record
        const currentRecord = await agilite.Connectors.execute('invoice_communications', 'read_by_id', {
          recordId
        })

        if (!currentRecord.data) {
          reject(new Error('Communications record not found'))
          return
        }

        // Update the communication status
        const updatedData = {
          ...currentRecord.data,
          communicationStatus: statusData,
          modifiedAt: new Date().toISOString(),
          modifiedBy: store.getState().auth.agiliteUser?._id
        }

        // Update the record
        const response = await agilite.Connectors.execute('invoice_communications', 'update', {
          recordId,
          data: JSON.stringify(updatedData)
        })

        // Broadcast update via WebSocket
        if (response.data) {
          sendRecord(response.data, 'invoice_communications', 'status_updated')
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Add a note to communications record
 * @param {string} recordId - Record ID
 * @param {Object} noteData - Note data
 * @returns {Promise} Updated communications record
 */
export const addNote = (recordId, noteData) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // First get the current record
        const currentRecord = await agilite.Connectors.execute('invoice_communications', 'read_by_id', {
          recordId
        })

        if (!currentRecord.data) {
          reject(new Error('Communications record not found'))
          return
        }

        // Add the new note to the notes array
        const updatedData = {
          ...currentRecord.data,
          notes: [...(currentRecord.data.notes || []), noteData],
          modifiedAt: new Date().toISOString(),
          modifiedBy: store.getState().auth.agiliteUser?._id
        }

        // Update the record
        const response = await agilite.Connectors.execute('invoice_communications', 'update', {
          recordId,
          data: JSON.stringify(updatedData)
        })

        // Broadcast update via WebSocket
        if (response.data) {
          sendRecord(response.data, 'invoice_communications', 'note_added')
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Delete a note from communications record
 * @param {string} recordId - Record ID
 * @param {string} noteId - Note ID to delete
 * @returns {Promise} Updated communications record
 */
export const deleteNote = (recordId, noteId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // First get the current record
        const currentRecord = await agilite.Connectors.execute('invoice_communications', 'read_by_id', {
          recordId
        })

        if (!currentRecord.data) {
          reject(new Error('Communications record not found'))
          return
        }

        // Remove the note from the notes array
        const updatedData = {
          ...currentRecord.data,
          notes: (currentRecord.data.notes || []).filter((note) => note.id !== noteId),
          modifiedAt: new Date().toISOString(),
          modifiedBy: store.getState().auth.agiliteUser?._id
        }

        // Update the record
        const response = await agilite.Connectors.execute('invoice_communications', 'update', {
          recordId,
          data: JSON.stringify(updatedData)
        })

        // Broadcast update via WebSocket
        if (response.data) {
          sendRecord(response.data, 'invoice_communications', 'note_deleted')
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Delete a chat message from communications record
 * @param {string} recordId - Record ID
 * @param {number} messageIndex - Index of message to delete
 * @returns {Promise} Updated communications record
 */
export const deleteMessage = (recordId, messageIndex) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // First get the current record
        const currentRecord = await agilite.Connectors.execute('invoice_communications', 'read_by_id', {
          recordId
        })

        if (!currentRecord.data) {
          reject(new Error('Communications record not found'))
          return
        }

        // Remove the message from the chatMessages array
        const chatMessages = [...(currentRecord.data.chatMessages || [])]
        chatMessages.splice(messageIndex, 1)

        const updatedData = {
          ...currentRecord.data,
          chatMessages,
          modifiedAt: new Date().toISOString(),
          modifiedBy: store.getState().auth.agiliteUser?._id
        }

        // Update the record
        const response = await agilite.Connectors.execute('invoice_communications', 'update', {
          recordId,
          data: JSON.stringify(updatedData)
        })

        // Broadcast update via WebSocket
        if (response.data) {
          sendRecord(response.data, 'invoice_communications', 'message_deleted')
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get communications statistics for an entity
 * @param {Object} filter - Additional filter criteria
 * @returns {Promise} Communications statistics
 */
export const getCommsStats = (filter = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const aggregationPipeline = [
          {
            $match: {
              entityRef: store.getState().core.entity._id,
              ...filter
            }
          },
          {
            $group: {
              _id: null,
              totalComms: { $sum: 1 },
              emailsSent: {
                $sum: {
                  $cond: [{ $eq: ['$communicationStatus.emailSent', true] }, 1, 0]
                }
              },
              smsSent: {
                $sum: {
                  $cond: [{ $eq: ['$communicationStatus.smsSent', true] }, 1, 0]
                }
              },
              whatsappSent: {
                $sum: {
                  $cond: [{ $eq: ['$communicationStatus.whatsappSent', true] }, 1, 0]
                }
              },
              totalMessages: {
                $sum: { $size: { $ifNull: ['$chatMessages', []] } }
              },
              totalNotes: {
                $sum: { $size: { $ifNull: ['$notes', []] } }
              }
            }
          }
        ]

        const response = await agilite.Connectors.execute('invoice_communications', 'aggregate', {
          pipeline: JSON.stringify(aggregationPipeline)
        })
        resolve(response.data[0] || {})
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Search communications by criteria
 * @param {Object} searchCriteria - Search criteria
 * @param {number} page - Page number
 * @param {number} pageLimit - Page size
 * @returns {Promise} Search results
 */
export const searchComms = (searchCriteria = {}, page = 1, pageLimit = 20) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const filter = {
          entityRef: store.getState().core.entity._id,
          ...searchCriteria
        }

        const response = await agilite.Connectors.execute('invoice_communications', 'read', {
          filter: JSON.stringify(filter),
          page,
          pageLimit,
          sort: JSON.stringify({ createdAt: -1 })
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export default {
  getInvoiceComms,
  createInvoiceComms,
  updateInvoiceComms,
  addChatMessage,
  updateCommStatus,
  addNote,
  deleteNote,
  deleteMessage,
  getCommsStats,
  searchComms
}
